/* eslint-disable react/no-array-index-key */
// Rows map 1:1 to positional final-form paths (batch_values[i]).
import React from "react";
import { Field } from "react-final-form";
import { ExlyDropdown, ExlyInput } from "common/form";
import { ArrayLevelError } from "./ArrayLevelError";
import { asArray } from "../utils/tierOps";
import {
  ARRAY_ERROR_KEYS,
  BATCH_FUTURE_UID,
  LTO_FORM_KEYS,
  VALUE_UNIT,
} from "../constants";
import { OFFER_DIRECTION } from "../utils/unifiedOffer";
import { validateFlatFinalPrice } from "../limitedOfferValidators";

const VALUE_UNIT_OPTIONS = [
  { label: "%", value: VALUE_UNIT.PERCENT },
  { label: "₹ Flat", value: VALUE_UNIT.FLAT },
];

/**
 * Batch Wise (§5.3): direct per-batch table + "All future batches" default row.
 * structuralDisabled locks the unit toggle on edit; disabled locks values.
 */
const BatchWiseTable = ({
  values = {},
  form,
  showInternational = false,
  disabled = false,
  structuralDisabled = false,
  batchOptions = [],
  originalPrice = null,
}) => {
  const valueUnit = values[LTO_FORM_KEYS.VALUE_UNIT] || VALUE_UNIT.PERCENT;
  const isSurge = values[LTO_FORM_KEYS.DIRECTION] === OFFER_DIRECTION.SURGE;
  const validateValue = (value) =>
    valueUnit === VALUE_UNIT.FLAT
      ? validateFlatFinalPrice(value, originalPrice, { isSurge })
      : value === "" || value === null || value === undefined
      ? "Required"
      : undefined;
  const stored = asArray(values[LTO_FORM_KEYS.BATCH_VALUES]);
  const rows = stored.length
    ? stored
    : [
        ...batchOptions.map((b) => ({
          batch_uid: b.uuid || b.value,
          label: b.label || b.title,
          value: "",
        })),
        {
          batch_uid: BATCH_FUTURE_UID,
          label: "All future batches (default)",
          value: "",
        },
      ];
  const gridCols = showInternational
    ? "tw-grid-cols-[1.4fr_1fr_1fr]"
    : "tw-grid-cols-[1.4fr_1fr]";

  return (
    <div className="tw-w-full">
      <span className="tw-block tw-text-[13px] tw-font-semibold tw-mb-2">
        Price by Batch *
      </span>
      {/* value_unit is driven by Offer Type (Amount → ₹ Flat, Percentage → %).
          Hidden Field keeps the backend payload identical — no UI to change it. */}
      <div style={{ display: "none" }}>
        <Field
          name={LTO_FORM_KEYS.VALUE_UNIT}
          component={ExlyDropdown}
          options={VALUE_UNIT_OPTIONS}
          disabled={disabled || structuralDisabled}
          size="small"
        />
      </div>
      <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-[#E2E4EC] tw-overflow-hidden">
        <div
          className={`tw-grid ${gridCols} tw-gap-2.5 tw-border-0 tw-bg-[#F7F7FB] tw-px-3.5 tw-py-2.5 tw-text-[11px] tw-font-bold tw-text-[#6B7280]`}
        >
          <span>BATCH</span>
          <span>
            {isSurge ? "INCREASE" : "DECREASE"} PRICE{" "}
            {valueUnit === VALUE_UNIT.PERCENT ? "BY (%)" : "TO (₹)"}
          </span>
          {showInternational ? <span>INTERNATIONAL ($)</span> : null}
        </div>
        {rows.map((row, idx) => (
          <div
            key={row.batch_uid || `batch-${idx}`}
            className={`tw-grid ${gridCols} tw-items-center tw-gap-2.5 tw-mx-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-[#E2E4EC] tw-py-2.5`}
          >
            <span className="tw-text-[13px] tw-font-medium">
              {row.label || `Batch ${idx + 1}`}
            </span>
            <Field
              name={`${LTO_FORM_KEYS.BATCH_VALUES}[${idx}].value`}
              component={ExlyInput}
              type="number"
              placeholder="e.g. 10"
              fullWidth
              disabled={disabled}
              validate={validateValue}
            />
            {showInternational ? (
              <Field
                name={`${LTO_FORM_KEYS.BATCH_VALUES}[${idx}].value_international`}
                component={ExlyInput}
                type="number"
                placeholder="e.g. 10"
                fullWidth
                disabled={disabled}
              />
            ) : null}
          </div>
        ))}
      </div>
      <p className="tw-mt-2 tw-text-[12px] tw-text-gray-500">
        New batches automatically use the &quot;All future batches&quot; value
        until you set one specifically for them.
      </p>
      <ArrayLevelError errorKey={ARRAY_ERROR_KEYS.BATCH} />
      <SeedBatchRows rows={rows} form={form} />
    </div>
  );
};

// Seeds the row list into form state (no leaf Field on the array path —
// that conflicts with the nested batch_values[i].value fields). Re-seeds
// when the options resolve async (listing-details classes arrive after
// mount), but never overwrites existing rows (edit mode / user input).
const SeedBatchRows = ({ rows, form }) => {
  const seededKey = React.useRef(null);
  const key = JSON.stringify((rows || []).map((r) => r.batch_uid));
  React.useEffect(() => {
    const current = form?.getState?.().values?.[LTO_FORM_KEYS.BATCH_VALUES];
    if (
      (!Array.isArray(current) || current.length === 0) &&
      rows.length > 0 &&
      seededKey.current !== key
    ) {
      seededKey.current = key;
      form.change(LTO_FORM_KEYS.BATCH_VALUES, rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return null;
};

export { BatchWiseTable };
